import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import type { ShoppingList, ShoppingListItem } from '../types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Sorts shopping lists by createdAt timestamp in descending order (newest first).
 * This ensures consistent sorting behavior across the application.
 *
 * IMPORTANT: This function assumes all lists have numeric createdAt timestamps.
 * For offline-first consistency, these should be client-generated timestamps
 * that are preserved by the backend during sync operations.
 *
 * @param lists - Array of shopping lists to sort
 * @returns New array with lists sorted by creation time (newest first)
 *
 * @see SORTING_AND_TIMESTAMPS.md for detailed explanation of timestamp handling
 */
export const sortListsByCreatedAt = (lists: ShoppingList[]): ShoppingList[] => {
  return [...lists].sort((a, b) => b.createdAt - a.createdAt);
};

/**
 * Sorts shopping list items by createdAt timestamp in descending order (newest first).
 * This ensures consistent sorting behavior across the application.
 *
 * IMPORTANT: This function assumes all items have numeric createdAt timestamps.
 * For offline-first consistency, these should be client-generated timestamps
 * that are preserved by the backend during sync operations.
 *
 * This function handles both temporary IDs (temp-*) and real IDs, ensuring
 * proper sorting regardless of sync status.
 *
 * @param items - Array of shopping list items to sort
 * @returns New array with items sorted by creation time (newest first)
 *
 * @see SORTING_AND_TIMESTAMPS.md for detailed explanation of timestamp handling
 */
export const sortItemsByCreatedAt = (items: ShoppingListItem[]): ShoppingListItem[] => {
  return [...items].sort((a, b) => b.createdAt - a.createdAt);
};

/**
 * Orders items for list-detail rendering.
 *
 * The base app convention is newest-created item first. When grouping is enabled,
 * checked items are partitioned below unchecked items without changing the
 * selected order inside either group.
 */
export const orderItemsForListDisplay = (
  items: Array<ShoppingListItem | null | undefined>,
  groupDoneItems: boolean,
  itemOrder?: string[],
): ShoppingListItem[] => {
  const validItems = items.filter((item): item is ShoppingListItem => Boolean(item?.id));
  const persistedOrder = new Map((itemOrder ?? []).map((id, index) => [id, index]));
  const sortedItems = persistedOrder.size === 0
    ? sortItemsByCreatedAt(validItems)
    : [...validItems].sort((a, b) => {
      const aIndex = persistedOrder.get(a.id);
      const bIndex = persistedOrder.get(b.id);

      if (aIndex !== undefined && bIndex !== undefined) {
        return aIndex - bIndex;
      }
      if (aIndex !== undefined) return -1;
      if (bIndex !== undefined) return 1;
      return b.createdAt - a.createdAt;
    });

  if (!groupDoneItems) {
    return sortedItems;
  }

  const uncheckedItems = sortedItems.filter(item => !item.checked);
  const checkedItems = sortedItems.filter(item => item.checked);

  return [...uncheckedItems, ...checkedItems];
};

/**
 * Adds an item ID to the top of a persisted order without duplicate IDs.
 */
export const prependItemToOrder = (
  itemOrder: string[] | undefined,
  itemId: string,
): string[] | undefined => {
  if (!itemOrder) return undefined;

  return [itemId, ...new Set(itemOrder.filter(existingId => existingId !== itemId))];
};

/**
 * Replaces an optimistic item ID with its server ID at the same order position.
 */
export const replaceItemInOrder = (
  itemOrder: string[] | undefined,
  previousItemId: string,
  itemId: string,
): string[] | undefined => {
  if (!itemOrder) return undefined;

  const uniqueOrder = [...new Set(itemOrder)];
  const previousIndex = uniqueOrder.indexOf(previousItemId);
  const nextOrder = uniqueOrder.filter(
    existingId => existingId !== previousItemId && existingId !== itemId,
  );

  if (previousIndex === -1) {
    return [itemId, ...nextOrder];
  }

  nextOrder.splice(Math.min(previousIndex, nextOrder.length), 0, itemId);
  return nextOrder;
};

/**
 * Translates a message that might be a translation key from the backend
 * @param message - The message from backend (could be a translation key or plain text)
 * @param t - The translation function from useTranslation
 * @param interpolationValues - Optional interpolation values for the translation
 * @returns The translated message or the original message if it's not a translation key
 */
export const translateBackendMessage = (
  message: string | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
  interpolationValues?: Record<string, unknown>
): string => {
  if (!message) return '';

  // Check if the message looks like a translation key (contains dots and common patterns)
  if (
    message.includes('.') && (
      message.includes('stripe.') ||
      message.includes('errors.') ||
      message.includes('messages.') ||
      message.includes('referrals.')
    )
  ) {
    try {
      // Try to translate it as a key with interpolation values if provided
      const translated = t(message, interpolationValues);

      // If the translation returns the same key, it means the key doesn't exist
      // In that case, return the original message
      if (translated === message) {
        console.warn(`Translation key not found: ${message}`);
        return message;
      }

      return translated;
    } catch (error) {
      console.error(`Translation error for key ${message}:`, error);
      // If translation fails, return the original message
      return message;
    }
  }

  // If it doesn't look like a translation key, return as is
  return message;
};
